// Teacher Subjects Tab - SAT V4 Zone
import { DsButton, DsCard, DsIcon, DsTypography } from '@ice-web-app/designSystem';
import { Box, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, IconButton, InputLabel, MenuItem, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Tooltip } from '@mui/material';
import type { FC } from 'react';
import { useState } from 'react';
import { WeekToggle } from '../../../../lib/components';
import type { WeekType } from '../../../../lib/components';
import { useSettingsStore } from '../../../../stores';
import type { Teacher, Subject, Class, YearGroup } from '../../../../types';
import { mockSubjects, mockClasses, mockRooms } from '../../../../lib/mockData';
import { getPeriodsColumnHeader, getSubjectPeriodsValue } from '../../../../lib/utils';

const YEAR_GROUPS: YearGroup[] = [7, 8, 9, 10, 11];

interface SubjectsTabProps {
  teacher: Teacher;
  onUpdate?: (updates: Partial<Teacher>) => void;
}

const SubjectsTab: FC<SubjectsTabProps> = ({ teacher, onUpdate }) => {
  const { scheduleType } = useSettingsStore();
  const [selectedWeek, setSelectedWeek] = useState<WeekType>('A');

  // Subject modals
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [editSubjectModalOpen, setEditSubjectModalOpen] = useState(false);
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>(teacher.subjects);
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);

  // Class modals
  const [addClassModalOpen, setAddClassModalOpen] = useState(false);
  const [editClassModalOpen, setEditClassModalOpen] = useState(false);
  const [classForm, setClassForm] = useState({
    name: '',
    subjectId: '',
    yearGroup: 7 as YearGroup,
    roomId: '',
    set: 1,
    band: 'X' as 'X' | 'Y' | 'Lower' | 'Higher',
    periods: 5
  });
  const [editingClass, setEditingClass] = useState<Class | null>(null);

  const teacherSubjects = mockSubjects.filter((s) =>
    teacher.subjects.some(subjectCode => s.code.toLowerCase() === subjectCode.toLowerCase() || s.id === subjectCode)
  );
  const teacherClasses = mockClasses.filter((c) => c.teacherId === teacher.id);

  // Subject handlers
  const handleOpenAssign = () => {
    setSelectedSubjects(teacher.subjects);
    setAssignModalOpen(true);
  };

  const handleToggleSubject = (subjectId: string) => {
    setSelectedSubjects(prev =>
      prev.includes(subjectId)
        ? prev.filter(id => id !== subjectId)
        : [...prev, subjectId]
    );
  };

  const handleSaveSubjects = () => {
    onUpdate?.({ subjects: selectedSubjects });
    setAssignModalOpen(false);
  };

  const handleOpenEditSubject = (subject: Subject) => {
    setEditingSubject(subject);
    setEditSubjectModalOpen(true);
  };

  const handleSaveEditSubject = () => {
    // Mock save - in real app would call API
    setEditSubjectModalOpen(false);
    setEditingSubject(null);
  };

  // Class handlers
  const handleOpenAddClass = () => {
    setClassForm({
      name: '',
      subjectId: teacherSubjects[0]?.id || '',
      yearGroup: 7,
      roomId: mockRooms[0]?.id || '',
      set: 1,
      band: 'X',
      periods: 5,
    });
    setAddClassModalOpen(true);
  };

  const handleOpenEditClass = (cls: Class) => {
    setEditingClass(cls);
    setClassForm({
      name: cls.name,
      subjectId: cls.subjectId,
      yearGroup: cls.yearGroup,
      roomId: cls.roomId || '',
      set: 1,
      band: 'X',
      periods: 5,
    });
    setEditClassModalOpen(true);
  };

  const handleAddClass = () => {
    if (classForm.name && classForm.subjectId) {
      // Mock add - in real app would call API
      setAddClassModalOpen(false);
      setClassForm({ name: '', subjectId: '', yearGroup: 7, roomId: '', set: 1, band: 'X', periods: 5 });
    }
  };

  const handleSaveEditClass = () => {
    if (editingClass && classForm.name) {
      // Mock save - in real app would call API
      setEditClassModalOpen(false);
      setEditingClass(null);
      setClassForm({ name: '', subjectId: '', yearGroup: 7, roomId: '', set: 1, band: 'X', periods: 5 });
    }
  };

  const getClassesBySubject = (subjectId: string) => {
    return teacherClasses.filter((c) => c.subjectId === subjectId);
  };

  return (
    <Stack spacing={3}>
      {/* Summary */}
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4}>
          <DsCard sx={{ p: 2, textAlign: 'center' }}>
            <DsTypography variant="h4" fontWeight={700} color="primary.main">
              {teacherSubjects.length}
            </DsTypography>
            <DsTypography variant="caption" color="text.secondary">
              Subjects
            </DsTypography>
          </DsCard>
        </Grid>
        <Grid item xs={6} sm={4}>
          <DsCard sx={{ p: 2, textAlign: 'center' }}>
            <DsTypography variant="h4" fontWeight={700} color="secondary.main">
              {teacherClasses.length}
            </DsTypography>
            <DsTypography variant="caption" color="text.secondary">
              Classes Assigned
            </DsTypography>
          </DsCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <DsCard sx={{ p: 2, textAlign: 'center' }}>
            <DsTypography variant="h4" fontWeight={700} color="success.main">
              {teacherClasses.reduce((sum, c) => sum + c.pupilIds.length, 0)}
            </DsTypography>
            <DsTypography variant="caption" color="text.secondary">
              Total Pupils
            </DsTypography>
          </DsCard>
        </Grid>
      </Grid>

      {/* Subject List
      <DsCard sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <DsTypography variant="h6" fontWeight={600}>
            Assigned Subjects
          </DsTypography>
          {onUpdate && (
            <DsButton variant="outlined" size="small" startIcon={<DsIcon icon="PLUS_CIRCLE" />} onClick={handleOpenAssign}>
              Assign Subject
            </DsButton>
          )}
        </Stack>

        <Grid container spacing={2}>
          {teacherSubjects.map((subject) => {
            const subjectClasses = getClassesBySubject(subject.id);
            const totalPupils = subjectClasses.reduce((sum, c) => sum + c.pupilIds.length, 0);

            return (
              <Grid item xs={12} md={6} key={subject.id}>
                <DsCard
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderLeft: 4,
                    borderColor: subject.color,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <DsTypography variant="subtitle1" fontWeight={600}>
                        {subject.name}
                      </DsTypography>
                      <DsTypography variant="caption" color="text.secondary">
                        {subject.code} • {subject.department}
                      </DsTypography>
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip
                        label={`${subjectClasses.length} classes`}
                        size="small"
                        sx={{
                          bgcolor: `${subject.color}20`,
                          color: subject.color,
                        }}
                      />
                      {onUpdate && (
                        <Tooltip title="Edit Subject">
                          <IconButton size="small" onClick={() => handleOpenEditSubject(subject)}>
                            <DsIcon icon="EDIT" sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>
                  <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                    <DsTypography variant="body2" color="text.secondary">
                      {totalPupils} pupils
                    </DsTypography>
                    <DsTypography variant="body2" color="text.secondary">
                      {Object.values(subject.periodsPerWeek).join('/')} periods/week
                    </DsTypography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                    {subject.yearGroups.map((year) => (
                      <Chip key={year} label={`Y${year}`} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </DsCard>
              </Grid>
            );
          })}
        </Grid>

        {teacherSubjects.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default', borderRadius: 2 }}>
            <DsTypography color="text.secondary">
              No subjects assigned yet.
            </DsTypography>
          </Box>
        )}
      </DsCard> */}

      {/* Classes Table */}
      <DsCard sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <DsTypography variant="h6" fontWeight={600}>
            Classes
          </DsTypography>
          {onUpdate && (
            <DsButton variant="outlined" size="small" startIcon={<DsIcon icon="PLUS_CIRCLE" />} onClick={handleOpenAddClass}>
              Add Class
            </DsButton>
          )}
        </Stack>
        {scheduleType === 'Bi-Weekly' && (
          <WeekToggle selectedWeek={selectedWeek} onChange={setSelectedWeek} />
        )}
        {teacherClasses.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">Year Group</TableCell>
                  <TableCell>Class Name</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell align="center">Sets/Bands</TableCell>
                  <TableCell align="center">Pupils</TableCell>
                  <TableCell align="center">{getPeriodsColumnHeader(scheduleType)}</TableCell>
                  {onUpdate && <TableCell align="center">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {teacherClasses.map((cls) => {
                  const subject = mockSubjects.find((s) => s.id === cls.subjectId);
                  // Generate year group color based on year
                  const yearColors: Record<number, string> = {
                    7: '#4CAF50',
                    8: '#2196F3',
                    9: '#9C27B0',
                    10: '#FF9800',
                    11: '#F44336',
                  };
                  const yearColor = yearColors[cls.yearGroup] || '#9E9E9E';

                  return (
                    <TableRow key={cls.id} hover>
                      <TableCell align="center">
                        <Chip
                          label={`Year ${cls.yearGroup}`}
                          size="small"
                          sx={{
                            bgcolor: `${yearColor}20`,
                            color: yearColor,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <DsTypography variant="body2" fontWeight={500}>
                          {cls.name}
                        </DsTypography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: subject?.color || '#9E9E9E',
                            }}
                          />
                          <span>{subject?.name || 'Unknown'}</span>
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <DsTypography variant="body2" color="text.secondary">
                          {/* Alternate between Sets and Bands based on class */}
                          {cls.id.includes('eng') || cls.id.includes('mat') ?
                            `Set ${(Number.parseInt(cls.id.match(/\d+/)?.[0] || '1') % 5) + 1}` :
                            ['Band X', 'Band Y', 'Band Lower', 'Band Higher'][Number.parseInt(cls.id.match(/\d+/)?.[0] || '1') % 4]
                          }
                        </DsTypography>
                      </TableCell>
                      <TableCell align="center">{cls.pupilIds.length}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={subject ? getSubjectPeriodsValue(subject, cls.yearGroup, scheduleType, scheduleType === 'Bi-Weekly' ? selectedWeek : undefined) : '-'}
                          size="small"
                          sx={{
                            bgcolor: 'primary.main',
                            color: '#fff',
                          }}
                        />
                      </TableCell>
                      {onUpdate && (
                        <TableCell align="center">
                          <Tooltip title="Edit Class">
                            <IconButton size="small" color="primary" onClick={() => handleOpenEditClass(cls)}>
                              <DsIcon icon="EDIT" sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default', borderRadius: 2 }}>
            <DsTypography color="text.secondary">
              No classes assigned yet.
            </DsTypography>
            {onUpdate && (
              <DsButton variant="contained" size="small" sx={{ mt: 2 }} startIcon={<DsIcon icon="PLUS_CIRCLE" />} onClick={handleOpenAddClass}>
                Add First Class
              </DsButton>
            )}
          </Box>
        )}
      </DsCard>

      {/* Assign Subject Modal */}
      <Dialog open={assignModalOpen} onClose={() => setAssignModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Subjects</DialogTitle>
        <DialogContent>
          <DsTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select subjects this teacher can teach:
          </DsTypography>
          <Stack spacing={1} sx={{ maxHeight: 400, overflow: 'auto' }}>
            {mockSubjects.map((subject) => (
              <Box
                key={subject.id}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  border: 1,
                  borderColor: selectedSubjects.includes(subject.id) ? 'primary.main' : 'divider',
                  bgcolor: selectedSubjects.includes(subject.id) ? 'primary.light' : 'transparent',
                  cursor: 'pointer',
                }}
                onClick={() => handleToggleSubject(subject.id)}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedSubjects.includes(subject.id)}
                      onChange={() => handleToggleSubject(subject.id)}
                    />
                  }
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: subject.color }} />
                      <span>{subject.name}</span>
                      <Chip label={subject.code} size="small" variant="outlined" />
                      <Chip label={subject.department} size="small" />
                    </Stack>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <DsButton variant="outlined" onClick={() => setAssignModalOpen(false)}>Cancel</DsButton>
          <DsButton variant="contained" onClick={handleSaveSubjects}>
            Save ({selectedSubjects.length} subjects)
          </DsButton>
        </DialogActions>
      </Dialog>

      {/* Edit Subject Modal */}
      <Dialog open={editSubjectModalOpen} onClose={() => setEditSubjectModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Subject Assignment</DialogTitle>
        <DialogContent>
          {editingSubject && (
            <Stack spacing={3} sx={{ pt: 1 }}>
              <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, borderLeft: 4, borderColor: editingSubject.color }}>
                <DsTypography variant="h6">{editingSubject.name}</DsTypography>
                <DsTypography variant="body2" color="text.secondary">
                  {editingSubject.code} • {editingSubject.department}
                </DsTypography>
              </Box>
              <DsTypography variant="subtitle2">Year Groups Teaching</DsTypography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {editingSubject.yearGroups.map((year) => (
                  <Chip key={year} label={`Year ${year}`} color="primary" variant="outlined" />
                ))}
              </Stack>
              <DsTypography variant="subtitle2">Classes for this Subject</DsTypography>
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                {getClassesBySubject(editingSubject.id).length > 0 ? (
                  getClassesBySubject(editingSubject.id).map(cls => (
                    <Chip key={cls.id} label={cls.name} sx={{ m: 0.5 }} />
                  ))
                ) : (
                  <DsTypography variant="body2" color="text.secondary">No classes assigned for this subject</DsTypography>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <DsButton variant="outlined" onClick={() => setEditSubjectModalOpen(false)}>Close</DsButton>
          <DsButton variant="contained" onClick={handleSaveEditSubject}>Save Changes</DsButton>
        </DialogActions>
      </Dialog>

      {/* Add Class Modal */}
      <Dialog open={addClassModalOpen} onClose={() => setAddClassModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Class</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Class Name"
              value={classForm.name}
              onChange={(e) => setClassForm({ ...classForm, name: e.target.value })}
              placeholder="e.g., 7JW, 10FP, 9SM"
            />
            <FormControl fullWidth>
              <InputLabel>Subject</InputLabel>
              <Select
                value={classForm.subjectId}
                label="Subject"
                onChange={(e) => setClassForm({ ...classForm, subjectId: e.target.value })}
              >
                {teacherSubjects.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: s.color }} />
                      <span>{s.name}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Year Group</InputLabel>
                  <Select
                    value={classForm.yearGroup}
                    label="Year Group"
                    onChange={(e) => setClassForm({ ...classForm, yearGroup: e.target.value as YearGroup })}
                  >
                    {YEAR_GROUPS.map((y) => (
                      <MenuItem key={y} value={y}>Year {y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Periods</InputLabel>
                  <Select
                    value={classForm.periods}
                    label="Periods"
                    onChange={(e) => setClassForm({ ...classForm, periods: e.target.value as number })}
                  >
                    {Array.from({ length: 30 }, (_, i) => i + 1).map((num) => (
                      <MenuItem key={num} value={num}>{num}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Sets</InputLabel>
                  <Select
                    value={classForm.set}
                    label="Sets"
                    onChange={(e) => setClassForm({ ...classForm, set: e.target.value as number })}
                  >
                    {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                      <MenuItem key={num} value={num}>Set {num}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Bands</InputLabel>
                  <Select
                    value={classForm.band}
                    label="Bands"
                    onChange={(e) => setClassForm({ ...classForm, band: e.target.value as 'X' | 'Y' | 'Lower' | 'Higher' })}
                  >
                    <MenuItem value="X">Band X</MenuItem>
                    <MenuItem value="Y">Band Y</MenuItem>
                    <MenuItem value="Lower">Band Lower</MenuItem>
                    <MenuItem value="Higher">Band Higher</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <FormControl fullWidth>
              <InputLabel>Preferred Room</InputLabel>
              <Select
                value={classForm.roomId}
                label="Preferred Room"
                onChange={(e) => setClassForm({ ...classForm, roomId: e.target.value })}
              >
                {mockRooms.map((r) => (
                  <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <DsButton variant="outlined" onClick={() => setAddClassModalOpen(false)}>Cancel</DsButton>
          <DsButton variant="contained" onClick={handleAddClass} disabled={!classForm.name || !classForm.subjectId}>
            Add Class
          </DsButton>
        </DialogActions>
      </Dialog>

      {/* Edit Class Modal */}
      <Dialog open={editClassModalOpen} onClose={() => setEditClassModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Class</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Class Name"
              value={classForm.name}
              onChange={(e) => setClassForm({ ...classForm, name: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Subject</InputLabel>
              <Select
                value={classForm.subjectId}
                label="Subject"
                onChange={(e) => setClassForm({ ...classForm, subjectId: e.target.value })}
              >
                {teacherSubjects.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: s.color }} />
                      <span>{s.name}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Year Group</InputLabel>
                  <Select
                    value={classForm.yearGroup}
                    label="Year Group"
                    onChange={(e) => setClassForm({ ...classForm, yearGroup: e.target.value as YearGroup })}
                  >
                    {YEAR_GROUPS.map((y) => (
                      <MenuItem key={y} value={y}>Year {y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Periods</InputLabel>
                  <Select
                    value={classForm.periods}
                    label="Periods"
                    onChange={(e) => setClassForm({ ...classForm, periods: e.target.value as number })}
                  >
                    {Array.from({ length: 30 }, (_, i) => i + 1).map((num) => (
                      <MenuItem key={num} value={num}>{num}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Sets</InputLabel>
                  <Select
                    value={classForm.set}
                    label="Sets"
                    onChange={(e) => setClassForm({ ...classForm, set: e.target.value as number })}
                  >
                    {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                      <MenuItem key={num} value={num}>Set {num}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Bands</InputLabel>
                  <Select
                    value={classForm.band}
                    label="Bands"
                    onChange={(e) => setClassForm({ ...classForm, band: e.target.value as 'X' | 'Y' | 'Lower' | 'Higher' })}
                  >
                    <MenuItem value="X">Band X</MenuItem>
                    <MenuItem value="Y">Band Y</MenuItem>
                    <MenuItem value="Lower">Band Lower</MenuItem>
                    <MenuItem value="Higher">Band Higher</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <FormControl fullWidth>
              <InputLabel>Preferred Room</InputLabel>
              <Select
                value={classForm.roomId}
                label="Preferred Room"
                onChange={(e) => setClassForm({ ...classForm, roomId: e.target.value })}
              >
                {mockRooms.map((r) => (
                  <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {editingClass && (
              <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <DsTypography variant="body2" color="text.secondary">
                  <strong>{editingClass.pupilIds.length}</strong> pupils enrolled in this class
                </DsTypography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <DsButton variant="outlined" onClick={() => setEditClassModalOpen(false)}>Cancel</DsButton>
          <DsButton variant="contained" onClick={handleSaveEditClass} disabled={!classForm.name}>
            Save Changes
          </DsButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SubjectsTab;

